import "./LocationCard.scss";
import { BrandLogoDto, LocationDto } from "../../../generated/account-client";
import { Button } from "../misc/Button";
import { CheckOutlined, EllipsisOutlined, UserOutlined } from "@ant-design/icons";
import { Dropdown, Menu } from "antd";
import { MenuInfo } from "rc-menu/lib/interface";
import AccountsApi from "../../../api/AccountsApi";
import { useRecoilState } from "recoil";
import { error } from "../../../service/MessageService";
import { useEffect, useState } from "react";
import BrandsApi from "../../../api/BrandsApi";
import { getImagePath, ellipsis } from "../../../util/utils";
import { States } from "../../../util/countries";
import MainAppLocationIcon from "../../icons/MainAppLocationIcon";
import { memberState } from "../../../state/atoms";

export interface LocationCardProps {
  location: LocationDto;
  primaryLocation: LocationDto;
  disableRemove: boolean;
  onEdit: (location: LocationDto) => void;
  onRemove: (location: LocationDto) => void;
  onSetPrimary: (location: LocationDto) => void;
  managersCount: number;
}

export default function LocationCard({
  location,
  primaryLocation,
  disableRemove,
  managersCount,
  onEdit,
  onRemove,
  onSetPrimary,
}: LocationCardProps) {
  const [member] = useRecoilState(memberState);
  const [logo, setLogo] = useState({} as BrandLogoDto);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [fullAddress, setFullAddress] = useState("");

  useEffect(() => {
    if (location.brandId) getBrand(location.brandId);
    const state = getStateAbreviation(location.state || "") || location.state;
    setFullAddress(
      `${location.address} ${location.address2} ${location.city} ${state}`
    );
  }, [location, member.internalId]);

  function getBrand(id: string) {
    BrandsApi.getBrand(id).then((resp) => {
      const brand = resp.data;
      if (brand && brand.logos && brand.logos.length > 0) {
        setLogo(brand.logos[0]);
      } else {
        setLogo({} as BrandLogoDto);
      }
    });
  }

  function makeLocationPrimary() {
    AccountsApi.setPrimaryUserLocation(
      member.internalId,
      location.internalId || -1
    )
      .then(() => {
        onSetPrimary(location);
      })
      .catch((err) => {
        error(err.response.data.message);
      });
  }

  const toggleDropdown = (info: MenuInfo) => {
    info.domEvent.stopPropagation();
    info.domEvent.preventDefault();
    switch (+info.key) {
      case 0:
        setIsDropdownVisible(false);
        onEdit(location);
        return;
      case 1:
        setIsDropdownVisible(false);
        onRemove(location);
        return;
      case 2:
        makeLocationPrimary();
    }
  };

  const setPrimaryMenu = (
    <Menu className={"make-primary-menu"} onClick={toggleDropdown}>
      <Menu.Item key="0">Edit Location</Menu.Item>
      {!disableRemove && <Menu.Item key="1">Delete Location</Menu.Item>}
      <li className={"divider"} />
      <Menu.Item key="2">Make Primary Location</Menu.Item>
    </Menu>
  );

  const isPrimaryMenu = (
    <Menu className={"is-primary-menu"} onClick={toggleDropdown}>
      <Menu.Item key="0">Edit Location</Menu.Item>
      {!disableRemove && <Menu.Item key="1">Delete Location</Menu.Item>}
      <li className={"divider"} />
      <Menu.Item key="3">
        <CheckOutlined /> Primary Location
      </Menu.Item>
    </Menu>
  );

  function getStateAbreviation(value: string) {
    return States[value];
  }

  return (
    <div className={"card location-card"}>
      <Dropdown
        placement="bottomRight"
        arrow={false}
        visible={isDropdownVisible}
        className={"card-dropdown"}
        overlay={
          location.internalId === primaryLocation.internalId
            ? isPrimaryMenu
            : setPrimaryMenu
        }
        trigger={["click"]}
        onVisibleChange={() => setIsDropdownVisible(!isDropdownVisible)}
      >
        <Button id={location.id} type={"Link"}>
          <EllipsisOutlined />
        </Button>
      </Dropdown>
      <div className={"bg"} onClick={() => onEdit(location)} />

      <div
        className={Object.keys(logo).length ? "no-avatar" : "avatar"}
        onClick={() => onEdit(location)}
      >
        {Object.keys(logo).length ? (
          <img src={getImagePath(logo.path || "")} alt={""} loading="lazy" />
        ) : (
          <MainAppLocationIcon />
        )}
      </div>

      <section
        className={"location-card-description"}
        onClick={() => onEdit(location)}
      >
        <p className={"name"}>{location?.name}</p>
        <p>{location.address}</p>
        <p style={{ margin: 0 }}>
          {`${location.city}${location.city && location.state ? ", " : " "}${getStateAbreviation(location.state || "") || location.state
            }`}
        </p>
        <div className="user-count">
          <UserOutlined />
          {managersCount} {managersCount === 1 ? "user" : "users"}
        </div>
      </section>

      <section className={"mobile-location-card-description"}>
        <h4>{ellipsis(location?.name || "", 15)}</h4>
        <p>{ellipsis(fullAddress, 40)}</p>
      </section>
    </div>
  );
}
